import React, { useMemo, useRef } from 'react';
import { BackgroundThemeProps } from '../backgroundThemes';
import { useCanvasLoop } from '../useCanvasLoop';

interface Petal {
  x: number;
  y: number;
  size: number;
  fallSpeed: number;
  drift: number;
  swayOffset: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  colorIndex: number;
}

const DESKTOP_PARTICLE_COUNT = 50;
const MOBILE_PARTICLE_COUNT = 25;
const STATIC_PARTICLE_COUNT = 15;

const createSprite = (color: string) => {
  const sprite = document.createElement('canvas');
  const size = 48;
  sprite.width = size;
  sprite.height = size;
  const ctx = sprite.getContext('2d');

  if (!ctx) return sprite;

  ctx.translate(size / 2, size / 2);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -18);
  ctx.bezierCurveTo(16, -13, 18, 8, 1, 19);
  ctx.bezierCurveTo(-15, 8, -14, -12, 0, -18);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.38)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, -13);
  ctx.quadraticCurveTo(3, 0, -1, 14);
  ctx.stroke();

  return sprite;
};

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

export const PetalRainBackground: React.FC<BackgroundThemeProps> = ({ tone, activity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null!);

  const renderer = useMemo(() => {
    let petals: Petal[] = [];
    let sprites: HTMLCanvasElement[] = [];
    const palette =
      tone === 'dark'
        ? ['rgba(255, 210, 232, 0.92)', 'rgba(252, 231, 243, 0.86)', 'rgba(244, 114, 182, 0.74)']
        : ['rgba(236, 72, 153, 0.78)', 'rgba(244, 114, 182, 0.68)', 'rgba(251, 182, 206, 0.78)'];

    const createPetal = (size: { width: number; height: number }, aboveTop: boolean): Petal => ({
      x: randomBetween(0, size.width),
      y: aboveTop ? randomBetween(-size.height * 0.35, -24) : randomBetween(0, size.height),
      size: randomBetween(16, 34),
      fallSpeed: randomBetween(18, 48),
      drift: randomBetween(12, 36),
      swayOffset: randomBetween(0, Math.PI * 2),
      rotation: randomBetween(0, Math.PI * 2),
      rotationSpeed: randomBetween(-0.55, 0.55),
      opacity: randomBetween(0.42, 0.86),
      colorIndex: Math.floor(Math.random() * palette.length),
    });

    const drawPetal = (
      ctx: CanvasRenderingContext2D,
      petal: Petal,
      elapsedSeconds: number,
      size: { width: number; height: number }
    ) => {
      const sprite = sprites[petal.colorIndex];
      if (!sprite) return;

      const sway = Math.sin(petal.swayOffset + petal.y * 0.018) * petal.drift;
      const x = petal.x + sway;
      const drawSize = petal.size;

      if (x < -drawSize || x > size.width + drawSize || petal.y < -drawSize) {
        return;
      }

      ctx.save();
      ctx.globalAlpha = petal.opacity;
      ctx.translate(x, petal.y);
      ctx.rotate(petal.rotation + elapsedSeconds * petal.rotationSpeed);
      ctx.drawImage(sprite, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
      ctx.restore();
    };

    return {
      setup(_ctx: CanvasRenderingContext2D, size: { width: number; height: number }) {
        sprites = palette.map(createSprite);
        const count = size.width >= 768 ? DESKTOP_PARTICLE_COUNT : MOBILE_PARTICLE_COUNT;
        petals = Array.from({ length: count }, () => createPetal(size, false));
      },
      draw(
        ctx: CanvasRenderingContext2D,
        size: { width: number; height: number },
        deltaMs: number
      ) {
        const elapsedSeconds = deltaMs / 1000;
        ctx.clearRect(0, 0, size.width, size.height);

        petals.forEach((petal) => {
          petal.y += petal.fallSpeed * elapsedSeconds;
          petal.x += Math.sin(petal.swayOffset) * elapsedSeconds * 4;
          petal.rotation += petal.rotationSpeed * elapsedSeconds;
          petal.swayOffset += elapsedSeconds * 0.8;

          if (petal.y > size.height + petal.size) {
            Object.assign(petal, createPetal(size, true));
          }

          drawPetal(ctx, petal, elapsedSeconds, size);
        });
      },
      drawStatic(ctx: CanvasRenderingContext2D, size: { width: number; height: number }) {
        ctx.clearRect(0, 0, size.width, size.height);
        Array.from({ length: STATIC_PARTICLE_COUNT }, () => createPetal(size, false)).forEach(
          (petal) => drawPetal(ctx, petal, 0, size)
        );
      },
    };
  }, [tone]);

  useCanvasLoop(canvasRef, renderer, activity);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
